/*
 *   File : calculator.js
 *   Author : https://evoqins.com
 *   Description : SIP/Lumpsum Calculator component
 *   Integrations : NA
 *   Version : 1.0.0
*/


import React, { useState, useEffect, useCallback } from "react";

//custom component
import { CustomTab } from "../Other";
import { CustomSlider } from "../Form-Elements";
import { _amountInWords, _formatToRupees } from "../../Helper";
import { SectionTitle } from "../Title";
import { Icon } from "../Icon";

import Colors from "../../Style/color.scss";

const MAX_RETURNS = 93428655870981125;
const MAX_INVESTMENT = 2653759517025603;
const INDEX_FUND_INTEREST_RATE = 14.5;

const CALCULATOR_TAB = [
    {
        id: 1,
        label: "SIP"
    },
    {
        id: 2,
        label: "Lumpsum"
    },
]

const Calculator = (props) => {

    const [tabIndex, setTabIndex] = useState(1);
    const [sip, setSip] = useState('');
    const [sipTenure, setSipTenure] = useState(15);
    const [sipReturnRate, setSipReturnRate] = useState(INDEX_FUND_INTEREST_RATE);

    const [lumpsum, setLumpsum] = useState('');
    const [lumpsumTenure, setLumpsumTenure] = useState(15);
    const [lumpsumReturnRate, setLumpsumReturnRate] = useState(INDEX_FUND_INTEREST_RATE);

    const [investedAmount, setInvestedAmount] = useState(0);
    const [returnAmount, setReturnAmount] = useState(0);
    const [totalAmount, setTotalAmount] = useState(0);

    const [annualStep, setAnnualStep] = useState(0);
    const [graphHeights, setGraphHeights] = useState({});
    const [showCalculator, setShowCalculator] = useState(true);


    // initial sip
    useEffect(() => {
        if (props.min_investment) {
            setSip(props.min_investment.min_invest_sip);
            setLumpsum(props.min_investment.min_invest);
        } else {
            setSip(5000);
            setLumpsum(25000);
        }
        setSipTenure(15);
        setLumpsumTenure(15);
    }, []);

    //graph handling
    useEffect(() => {
        setGraphHeights({
            invested_value: _getBarHeight(investedAmount, totalAmount)
        });

    }, [totalAmount, investedAmount]);

    function _getBarHeight(value, maxValue) {
        const height = (value / maxValue) * 100;
        return Math.max(height, 6); // minimum visible height
    };


    // sip calculation
    useEffect(() => {
        let monthly_investment = sip; // Monthly investment amount
        let interest_rate = props.type === 2 ? props.returns_percentage : sipReturnRate; // Annual interest rate       // returns % for Index-fund: 14.5%
        let years = sipTenure; // Number of years
        let step_up_percent = annualStep; // Annual step-up rate
        let inflation_rate = 0; // Annual inflation rate
        if (tabIndex === 1) {
            // _calculateSip(monthly_investment, interest_rate, years, step_up_percent, inflation_rate);
            _calculateSIP(monthly_investment, interest_rate, years);
        }

    }, [sipTenure, sip, annualStep, tabIndex, sipReturnRate, props.returns_percentage]);


    // lumpsum calculation
    useEffect(() => {
        let principal_amount = lumpsum;
        let interest_rate = props.type === 2 ? props.returns_percentage : lumpsumReturnRate; // Annual interest rate
        let years = lumpsumTenure; // Number of years
        if (tabIndex === 2) {
            _calculateLumpsum(principal_amount, interest_rate, years);
        }
    }, [lumpsumTenure, lumpsum, tabIndex, lumpsumReturnRate]);

    // graph height handler
    function _getGraphHeight(item_returns, max_returns) {
        let canvas_height = 80;
        let calculated_height = canvas_height * item_returns / max_returns;
        if (calculated_height > canvas_height) {
            return calculated_height = canvas_height
        }
        return calculated_height;



    }


    //calculate SIP
    function _calculateSip(monthly_investment, annual_interest_rate, years, stepup_rate, inflation_rate) {
        let future_value = 0;
        let monthly_rate = (annual_interest_rate / 12) / 100;
        let totalMonths = years * 12;

        for (let month = 1; month <= totalMonths; month++) {
            if ((month != 1) && (month - 1 % 12 == 0)) {
                monthly_investment = monthly_investment + (monthly_investment * stepup_rate / 100);
            }
            let adjusted_investment = monthly_investment * Math.pow(1 + stepup_rate / 100, Math.floor((month - 1) / 12));
            future_value += adjusted_investment;
            future_value *= (1 + monthly_rate);
            future_value /= (1 + inflation_rate / 100);

        }
        // calculate invested amount using annual step up
        let corpus = 0; // total invested amount
        let sip = monthly_investment; // SIP selected by customer
        let step_sip = stepup_rate; // step sip percentage
        let yearly_invested_sip = 0;
        for (let year = 1; year <= years; year++) {
            if (year != 1) {
                sip = sip + (sip * (step_sip / 100)); // updated sip value for selected year
            }

            yearly_invested_sip = sip * 12;
            corpus += yearly_invested_sip;
        }
        let total_investment = corpus;
        let total_return = future_value - total_investment;

        setInvestedAmount(total_investment);
        setTotalAmount(future_value);

    };

    function _calculateSIP(monthly = 0, annual_rate = 0, years = 0) {
        const r = annual_rate / 12 / 100;
        const n = years * 12;
        const future_value = monthly * (((Math.pow(1 + r, n) - 1) / r) * (1 + r));
        const total_investment = monthly * n;
        setInvestedAmount(total_investment);
        setTotalAmount(future_value);
    };


    // calculate Lumpsum 
    function _calculateLumpsum(principal_amount = 0, annual_rate = 0, years = 0) {
        const rate = annual_rate / 100;        // convert % to decimal
        const future_value = principal_amount * Math.pow(1 + rate, years);  // compound interest
        setInvestedAmount(principal_amount);
        setTotalAmount(future_value);
    }

    // calculate other fund 
    function _calculateSipOtherFund(monthly_investment, annual_interest_rate, years, stepup_rate, inflation_rate) {
        let future_value = 0;
        let monthly_rate = (annual_interest_rate / 12) / 100;
        let total_months = years * 12;

        for (let month = 1; month <= total_months; month++) {
            let adjusted_investment = monthly_investment * Math.pow(1 + stepup_rate / 100, Math.floor((month - 1) / 12));
            future_value += adjusted_investment;
            future_value *= (1 + monthly_rate);
            future_value /= (1 + inflation_rate / 100);
        }
        // setOtherFund(future_value.toFixed(2));
    }

    // calculate other funds lumsum
    function _calculateLumpsumOtherFund(principal_amount, annual_rate, years) {
        // Convert the annual rate to decimal
        const rate = annual_rate / 100;
        // Calculate the future value
        const future_value = principal_amount * Math.pow(1 + rate, years);
        // setOtherFund(future_value.toFixed(2));
    }
    //handle tab change
    const _handleTabChange = (id) => {
        setTabIndex(id);
    }

    const _handleAmountChange = (value) => {
        if (tabIndex === 1) {
            setSip(value);
        } else {
            setLumpsum(value);
        }
    }

    //handle anual step slider
    const _handleSlider = (value) => {
        if (tabIndex === 1) {
            setSipReturnRate(value);
        } else {
            setLumpsumReturnRate(value);
        }
        setAnnualStep(value);
    }

    // //handle tenure slider
    const _handleTenureSlider = (value) => {
        if (tabIndex === 1) {
            setSipTenure(value);
        } else {
            setLumpsumTenure(value);
        }
    };

    const _handleChangeTimePeriod = (value) => {
        if (value <= 40) {
            if (tabIndex === 1) {
                setSipTenure(value);
            } else {
                setLumpsumTenure(value);
            }
        }
    };

    const _handleChangeReturnRate = (value) => {
        if (value <= 50) {
            if (tabIndex === 1) {
                setSipReturnRate(value);
            } else {
                setLumpsumReturnRate(value);
            }
        }
    };

    const _handleChangeAmount = (value) => {
        if (value <= 10000000) {
            if (tabIndex === 1) {
                setSip(value);
            } else {
                setLumpsum(value);
            }
        }
    };


    // inner function
    const Input = useCallback((props) => {
        return (
            <input
                value={props?.value}
                type="number"
                className="calculator-input"
                placeholder="0"
                onChange={(event) => props.handleChange(event.target.value)}
            />
        );
    }, []);


    return (
        <div className="p-4 e-border-radius-16 e-bg-cultured">
            <div className="d-flex align-items-center justify-content-between cursor-pointer" onClick={() => setShowCalculator(prev => !prev)}>
                <div>
                    <SectionTitle title="Return calculator" fontFamily="e-primary-font-inter" className="e-font-20 e-text-eerie-black " />
                    <p className="mt-2 e-text-onyx e-font-14 e-primary-font-inter e-font-weight-400 line-height-18px mb-0">
                        See how your money could grow
                    </p>
                </div>
                <Icon
                    icon="arrow-down"
                    color={Colors.quartz}
                    className={`width-20px-res ${showCalculator ? "transform-rotate-min-180" : ""} w-40px cursor-pointer transition`} />
            </div>

            {
                (showCalculator || props.type !== 1) &&
                <>
                    <div className="d-flex gap-2 pb-3 pb-sm-4 mb-3 mt-4">
                        {/* tab component */}
                        {
                            CALCULATOR_TAB.map((data, key) => (
                                <CustomTab key={key}
                                    tabData={data}
                                    type={3}
                                    tabIndex={tabIndex}
                                    handleChange={_handleTabChange} />
                            ))
                        }
                    </div>

                    <div className="row flex-column">

                        <div className={`${props.type === 1 ? "col-12" : "col"}`}>

                            <div className="pt-sm-4 padding-32px-bottom">
                                <div className="e-display-flex justify-content-space-between">
                                    <h4 className="mb-0 e-text-dark-jet e-font-14 e-primary-font-inter e-font-weight-400 line-height-16px">
                                        {tabIndex === 1 ? "Monthly investment" : "Total investment amount"}
                                    </h4>
                                    <span className="d-flex justify-content-between align-items-center w-110px e-bg-alice-blue_4 e-border-radius-4 px-2 e-text-egyptian-blue e-font-14 e-alt-font-poppins e-font-weight-400 line-height-16px">
                                        <span>₹</span>
                                        <Input value={tabIndex === 1 ? sip : lumpsum}
                                            handleChange={_handleChangeAmount} />
                                        {/* {tabIndex === 1 ? sip.toLocaleString() : lumpsum.toLocaleString()} */}
                                    </span>
                                </div>
                                <div className="mt-3">
                                    <CustomSlider min={500}
                                        max={10000000}
                                        step={500}
                                        defaultValue={tabIndex === 1 ? sip : lumpsum}
                                        handleChange={(value) => _handleAmountChange(value)} />
                                </div>
                            </div>

                            <div className="padding-32px-bottom">
                                <div className="e-display-flex justify-content-space-between">
                                    <h4 className="mb-0 e-text-dark-jet e-font-14 e-primary-font-inter e-font-weight-400 line-height-16px">
                                        Expected return rate(p.a)
                                    </h4>
                                    <span className="d-flex align-items-center  w-110px text-end e-bg-alice-blue_4 e-border-radius-4 px-2 e-text-egyptian-blue e-font-14 e-alt-font-poppins e-font-weight-400 line-height-16px">
                                        <Input value={tabIndex === 1 ? sipReturnRate : lumpsumReturnRate}
                                            handleChange={_handleChangeReturnRate} />
                                        %
                                    </span>
                                </div>
                                <div className="mt-3">
                                    <CustomSlider min={1}
                                        max={50}
                                        step={0.5}
                                        defaultValue={tabIndex === 1 ? sipReturnRate : lumpsumReturnRate}
                                        handleChange={(value) => _handleSlider(value)} />
                                </div>
                            </div>

                            <div className="pb-4">
                                <div className="e-display-flex justify-content-space-between">
                                    <h4 className="mb-0 e-text-dark-jet e-font-14 e-primary-font-inter e-font-weight-400 line-height-16px">
                                        Time period
                                    </h4>
                                    <div className="d-flex align-items-center w-110px e-bg-alice-blue_4 e-border-radius-4 px-2 e-text-egyptian-blue e-font-14 e-alt-font-poppins e-font-weight-400 line-height-16px">
                                        <Input value={tabIndex === 1 ? sipTenure : lumpsumTenure}
                                            handleChange={_handleChangeTimePeriod} />
                                        {
                                            tabIndex === 1 ?
                                                `${sipTenure === 1 ? 'Yr' : 'Yrs'}`
                                                :
                                                `${lumpsumTenure === 1 ? 'Yr' : 'Yrs'}`
                                        }
                                    </div>
                                </div>
                                <div className="mt-3">
                                    <CustomSlider min={1}
                                        max={40}
                                        step={1}
                                        defaultValue={tabIndex === 1 ? sipTenure : lumpsumTenure}
                                        handleChange={(value) => _handleTenureSlider(value)} />
                                </div>
                            </div>



                            {/* Will be enabled in future || update next div pt-5 tp pt-3 */}
                            {
                                // props.type == 2 &&
                                // <div className="mb-0 e-text-sonic-silver e-font-14 e-alt-font-poppins e-font-weight-400 line-height-20px padding-42px-top">
                                //     <p>*Estimated return is calculated based on the funds {props.returns_period} returns {props.returns_percentage}%.</p>
                                // </div>

                            }

                            {/* <div className={`e-display-flex justify-content-space-between ${props.type == 1 ? "padding-42px-top" : "pt-5"} `}>
                                <p className="mb-0 e-text-charleston-green e-font-14 e-alt-font-poppins e-font-weight-400 line-height-20px">
                                    Invested amount
                                </p>
                                <p className="mb-0 e-text-charleston-green e-font-16 e-primary-font-inter e-font-weight-500 line-height-20px">
                                    ₹{_formatToRupees(investedAmount)}
                                </p>
                            </div>

                            <div className="pt-4 e-display-flex justify-content-space-between padding-42px-top">
                                <p className="mb-0 e-text-charleston-green e-font-14 e-alt-font-poppins e-font-weight-400 line-height-20px">
                                    Est. returns
                                </p>
                                <p className="mb-0 e-text-charleston-green e-font-16 e-primary-font-inter e-font-weight-500 line-height-20px">
                                    ₹{_formatToRupees(returnAmount)}
                                </p>
                            </div>

                            <div className="pt-4 e-display-flex justify-content-space-between padding-42px-top">
                                <p className="mb-0 e-text-egyptian-blue e-font-14 e-alt-font-poppins e-font-weight-600 line-height-20px">
                                    Total value
                                </p>
                                <p className="mb-0 e-text-egyptian-blue e-font-16 e-primary-font-inter e-font-weight-600 line-height-20px">
                                    ₹{_formatToRupees(totalAmount)}
                                </p>
                            </div> */}
                        </div>

                        {/* Returns comparison chart */}

                        {
                            props.type == 1 &&
                            <div className="col-12 pt-3">
                                <h4 className="e-text-eerie-black e-font-14-res e-font-16 e-primary-font-inter e-font-weight-600 line-height-20px">
                                    Returns comparison
                                </h4>
                                <p className="mb-0 e-text-dark-jet e-font-12-res e-font-14 e-primary-font-inter e-font-weight-400 line-height-22px">
                                    *The estimated returns are determined by analyzing the historical returns of the mutual funds.
                                </p>

                                <div className="row ">
                                    <div className="col-lg-6 col-12 pt-5 mx-auto">
                                        <div className="row mb-0">
                                            <div className="line-height-0 col-6 text-center d-flex e-flex-direction-column justify-content-end">
                                                <div>
                                                    <p className="mb-1 e-text-granite-gray e-font-12-res e-font-14 e-alt-font-poppins e-font-weight-500 line-height-20px">
                                                        ₹{totalAmount < 100000 ? _formatToRupees(totalAmount || 0) : _amountInWords(totalAmount || 0)}
                                                    </p>
                                                    <div class="progress progress-vertical" style={{ height: "100px" }}>
                                                        <div class="progress-bar progress-bar-striped e-bg-color-emerald h-100" >
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="line-height-0 px-0 col-6 text-center d-flex e-flex-direction-column justify-content-end">
                                                <div>
                                                    <p className="mb-1 e-text-granite-gray e-font-12-res e-font-14 e-alt-font-poppins e-font-weight-500 line-height-20px">
                                                        ₹{investedAmount < 100000 ? _formatToRupees(investedAmount || 0) : _amountInWords(investedAmount || 0)}
                                                    </p>

                                                    <div class="progress progress-vertical" style={{ height: graphHeights.invested_value > 0 ? graphHeights.invested_value : 20 }}>
                                                        <div class="progress-bar progress-bar-striped e-bg-color-platinum-gray h-100">
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <hr className="m-0 h-point-5px border-0 e-bg-light-gray opacity-75" />
                                        <div className="row mt-2">
                                            <div className="col-6 text-center">
                                                <p className="mb-0 e-text-eerie-black e-font-12 e-alt-font-poppins e-font-weight-500">
                                                    Est. returns
                                                </p>
                                            </div>
                                            <div className="col-6 text-center">
                                                <p className="text-center  mb-0 e-text-eerie-black e-font-12 e-alt-font-poppins e-font-weight-500">
                                                    Invested
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        }
                    </div>
                </>
            }

        </div >
    )
}
export default Calculator;

Calculator.defaultProps = {
    type: 1
}